/* eslint-disable default-param-last */
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';

import { ApiResponse } from '../types/apiTypes';
import { extractErrorMessage } from '../utils/errorUtils';

// 기본 Query Hook
export const useApiQuery = <TData = unknown, TError = Error>(
  queryKey: (string | number)[],
  queryFn: () => Promise<ApiResponse<TData>>,
  options?: Omit<
    UseQueryOptions<ApiResponse<TData>, TError, ApiResponse<TData>>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey,
    queryFn,
    ...options,
  });
};

// 기본 Mutation Hook
export const useApiMutation = <
  TData = unknown,
  TError = Error,
  TVariables = void,
>(
  mutationFn: (variables: TVariables) => Promise<ApiResponse<TData>>,
  options?: UseMutationOptions<ApiResponse<TData>, TError, TVariables>,
) => {
  return useMutation({
    mutationFn,
    ...options,
  });
};

// Query 무효화 Hook
export const useInvalidateQueries = () => {
  const queryClient = useQueryClient();

  return {
    invalidateQueries: (queryKey: (string | number)[]) => {
      queryClient.invalidateQueries({ queryKey });
    },
    invalidateAll: () => {
      queryClient.invalidateQueries();
    },
    removeQueries: (queryKey: (string | number)[]) => {
      queryClient.removeQueries({ queryKey });
    },
  };
};

// 페이지네이션을 위한 Hook
export const usePaginatedQuery = <TData = unknown>(
  baseQueryKey: string[],
  queryFn: (page: number, limit: number) => Promise<ApiResponse<TData[]>>,
  page: number = 1,
  limit: number = 10,
  options?: Omit<
    UseQueryOptions<ApiResponse<TData[]>>,
    'queryKey' | 'queryFn'
  > & { keepPreviousData?: boolean },
) => {
  return useApiQuery(
    [...baseQueryKey, 'paginated', page, limit],
    () => queryFn(page, limit),
    {
      keepPreviousData: true,
      ...options,
    },
  );
};

// 검색을 위한 Hook
export const useSearchQuery = <TData = unknown>(
  baseQueryKey: string[],
  queryFn: (searchTerm: string) => Promise<ApiResponse<TData[]>>,
  searchTerm: string,
  options?: Omit<UseQueryOptions<ApiResponse<TData[]>>, 'queryKey' | 'queryFn'>,
) => {
  return useApiQuery(
    [...baseQueryKey, 'search', searchTerm],
    () => queryFn(searchTerm),
    {
      enabled: searchTerm.length > 0,
      ...options,
    },
  );
};

// 에러 메시지를 추출하는 유틸리티 (호환성을 위해 유지)
export const getApiErrorMessage = (error: unknown): string => {
  return extractErrorMessage(error);
};
